<script setup lang="ts">
import { ruText as t } from '@/constants/ru-text';

import type { DogTagComponent } from '@/widgets/account-dogtag';
import type { DogtagComponentType } from '@/types/gloss';
import type { Clan } from '@/types/vortex';

import { getColor } from '../lib';
import type { DogtagView, Size } from '../model/types';
import Clantag from './clantag.vue';

const {
  components,
  clanInfo,
  size = 'SM',
} = defineProps<{
  components: Record<DogtagComponentType, DogTagComponent>;
  clanInfo?: null | Clan;
  size?: Size;
}>();

const emit = defineEmits<{
  getClanData: [];
}>();

// Get clan data ONLY if size is MD
if (size === 'MD') {
  emit('getClanData');
}

let isTintable = false;
const isOnlyPatch = !('background' in components) && 'patch' in components;

const validate = (data: Record<string, string | null>): [null | string[], DogtagView | null] => {
  const requiredFields = ['PATCH'];

  if (!isOnlyPatch) {
    requiredFields.push('BG_URL');
  }

  if (isTintable) {
    requiredFields.push('BG_COLOR');
    requiredFields.push('BG_BORDER_COLOR');
  }

  const isRequiredFields = requiredFields.every((i) => i in data && data[i] !== null);
  if (!isRequiredFields) {
    return [[t.get('component-dogtag-missing-required-field') ?? ''], null];
  }

  const dogtag: DogtagView = {
    BG_URL: data.BG_URL ?? null,
    BG_COLOR: data.BG_COLOR ?? null,

    BG_BORDER_URL: data.BG_BORDER ?? '',
    BG_BORDER_COLOR: data.BG_BORDER_COLOR ?? null,

    PATCH: data.PATCH ?? '',
    SYMBOL_OFFSET_Y: data.OFFSET ?? null,
  };

  return [null, dogtag];
};

const getComponentDetails = (type: DogtagComponentType) => {
  if (!components) {
    return;
  }
  const dataObj = components[type];
  switch (type) {
    case 'background': {
      const { id, isColorizable, symbolOffsetY } = dataObj;
      return {
        id,
        isColorizable,
        symbolOffsetY: symbolOffsetY ? symbolOffsetY * 100 : null,
      };
    }
    case 'background_color':
    case 'border_color': {
      return dataObj.color ? getColor(dataObj.color) : null;
    }
    case 'patch': {
      return dataObj.icons?.medium;
    }
    default: {
      return dataObj;
    }
  }
};

const getLayerSet = (): {
  error: null | string[];
  data: DogtagView | null;
} => {
  const rawData: Record<string, string | null> = {};
  try {
    if (!isOnlyPatch) {
      const { id, symbolOffsetY, isColorizable } = getComponentDetails('background');
      isTintable = Boolean(isColorizable);

      // Bg(texture) + border
      const textureComponentList = getComponentDetails('texture') as DogTagComponent | undefined;
      const texture = textureComponentList?.textureData?.find((i) => i.id === id);
      if (texture) {
        rawData.BG_URL = texture.background?.medium
          ? `url(https:${texture.background?.medium})`
          : '';
        rawData.BG_BORDER = texture.border?.medium ? `url(https:${texture.border?.medium})` : null;
      } else if ('background' in components) {
        rawData.BG_URL = components.background.icons?.medium
          ? `url(https:${components.background.icons?.medium})`
          : '';
      } else {
        rawData.BG_URL = '';
      }

      // Tint
      if (isTintable) {
        rawData.BG_COLOR = getComponentDetails('background_color');
        rawData.BG_BORDER_COLOR = getComponentDetails('border_color');
      }

      if (symbolOffsetY) {
        rawData.OFFSET = symbolOffsetY;
      }
    }

    // Patch + offset
    rawData.PATCH = `url('${getComponentDetails('patch')}')`;
  } catch (e) {
    console.error(e);
  }

  const [err, data] = validate(rawData);

  return { error: err, data };
};

let dogtagView: DogtagView | undefined = undefined;
try {
  const { data } = getLayerSet();
  if (data) {
    dogtagView = data;
  }
} catch (error) {
  console.error(error);
}

if (!dogtagView) {
  throw new Error('DogtagView is not defined');
}
const currentDogtagFull = ref<DogtagView>(dogtagView);
const { BG_URL, BG_COLOR, BG_BORDER_URL, BG_BORDER_COLOR, SYMBOL_OFFSET_Y, PATCH } =
  currentDogtagFull.value;

const BG = ref(`${BG_URL} no-repeat center / cover`);
if (BG_COLOR) {
  BG.value += `, ${BG_COLOR}`;
}

const BG_BORDER = ref(`${BG_BORDER_URL} no-repeat center / cover`);
if (BG_BORDER_COLOR) {
  BG_BORDER.value += `, ${BG_BORDER_COLOR}`;
}

const styleForPatch = computed(() => (SYMBOL_OFFSET_Y ? { top: `${SYMBOL_OFFSET_Y}%` } : {}));

const hasBackgroundClanTag = Boolean(components.background?.showClanTag) || false;
const hasPatchClanTag = Boolean(components.patch?.showClanTag) || false;
const isShowClanTag = hasBackgroundClanTag || hasPatchClanTag || false;
</script>

<template>
  <div class="wrap">
    <div
      v-if="BG_URL"
      :class="['dogtag', { tint: isTintable }]"
    />
    <div
      v-if="BG_BORDER_URL"
      class="border"
    />
    <div
      v-if="PATCH"
      class="patch"
      :style="styleForPatch"
    />

    <transition>
      <clantag
        v-if="isShowClanTag && clanInfo"
        v-show="clanInfo"
        :components="components"
        :clan-tag="clanInfo.tag"
        :size="size"
      />
    </transition>
  </div>
</template>

<style scoped lang="postcss">
.wrap {
  position: relative;

  width: 100%;
  height: 100%;

  & > * {
    position: absolute;
  }
}

.dogtag,
.border,
.patch {
  width: 100%;
  height: 100%;
  display: inline-block;
}

.dogtag,
.border {
  background-blend-mode: overlay;
  mask-size: cover;
}

.dogtag {
  filter: brightness(0.7);
  background: v-bind(BG);
  mask-image: v-bind(BG_URL);
}

.border {
  background: v-bind(BG_BORDER);
  mask-image: v-bind(BG_BORDER_URL);
}

.patch {
  background: v-bind(PATCH) no-repeat center / cover;
}
</style>
